import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { Camera, CheckCircle2, ChevronLeft, ChevronRight, Info, Trash2 } from "lucide-react";
import { IntlContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import Button from "ui/Button";
import { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";
import type { RegisterDraft } from "../types";
import blobToBase64 from "helpers/file/blobToBase64";
import {
    ACCEPTED_IMAGE_MIMES,
    PROFILE_PHOTO_MAX_BYTES,
    PROFILE_PHOTO_MAX_MEGABYTES,
} from "helpers/file/uploadConstraints";
import { getErrorResponseData } from "helpers/typing";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";
import { trackRegisterProviderProgress } from "hooks/register";

interface Props {
    provider: RegisterDraft | undefined;
    setProvider: (provider: RegisterDraft | undefined) => void;
}

type DraftPhoto = {
    name?: string;
    size?: number;
    type?: string;
    content?: string;
};

const RegisterProviderPhoto: React.FC<Props> = ({ provider, setProvider }) => {
    const intl = useContext(IntlContext);
    const stepper = useRegisterStepper();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [localFile, setLocalFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);

    useRegisterDraftGuard(provider, "prestations");

    const previewUrl = useMemo(() => {
        if (!localFile) return null;
        return URL.createObjectURL(localFile);
    }, [localFile]);

    useEffect(() => {
        if (!previewUrl) return;
        return () => URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const onSubmit = async (values: RegisterDraft) => {
        try {
            setProvider(Object.assign({}, provider, values));
            const email = String(values?.email ?? provider?.email ?? "").trim();
            if (email) {
                await trackRegisterProviderProgress({ email, step: "photo" });
            }
            trackRegisterStepComplete({
                registerType: "provider",
                stepKey: "photo",
                stepIndex: 5,
                stepLabel: intl.formatMessage("authentication.register.provider_flow.photo.label"),
            });
            stepper.goTo("cesu");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={provider}
                mutators={{ ...arrayMutators }}
                render={({ handleSubmit, submitting, form, values }) => {
                    const savedPhoto = (values as RegisterDraft | undefined)?.photo as DraftPhoto | undefined;
                    const savedPreviewSrc =
                        savedPhoto?.content && savedPhoto?.type
                            ? `data:${savedPhoto.type};base64,${savedPhoto.content}`
                            : null;
                    const previewSrc = previewUrl ?? savedPreviewSrc;
                    const photoTooLarge = (savedPhoto?.size ?? 0) > PROFILE_PHOTO_MAX_BYTES;
                    const sizeError =
                        fileError ??
                        (photoTooLarge
                            ? intl.formatMessage("generic.upload.too_large", { max: PROFILE_PHOTO_MAX_MEGABYTES })
                            : null);

                    return (
                        <form onSubmit={handleSubmit} className="register__form">
                            <div className="flex flex--direction-column gap-10">
                                <p className="h1 font-black">
                                    Votre photo de profil <span className="block text-primary">inspire confiance.</span>
                                </p>
                                <p className="h4 margin-bottom--2 margin-top--1">
                                    Avoir une photo de profil rassure vos voisins et vous donne plus de chance d'être contacté.
                                </p>

                                <div className="register__group-fields">
                                    <div className="flex flex-col gap-8">
                                        <div
                                            className={[
                                                "w-full rounded-[32px] p-10",
                                                (values as RegisterDraft | undefined)?.photo
                                                    ? "border-2 border-dashed border-emerald-400/80 bg-emerald-500/5"
                                                    : "border border-border/60 bg-primary/5",
                                            ].join(" ")}
                                        >
                                            <div className="flex flex-col items-center gap-5 text-center">
                                                <button
                                                    type="button"
                                                    className="w-full rounded-[28px] bg-transparent p-2 transition-colors hover:bg-background/40"
                                                    onClick={() => fileInputRef.current?.click()}
                                                >
                                                    <div className="flex flex-col items-center gap-2 py-2">
                                                        {previewSrc ? (
                                                            <div className="flex flex-col items-center gap-4">
                                                                <div className="relative h-28 w-28 rounded-full border border-input bg-muted overflow-visible">
                                                                    <div className="h-full w-full overflow-hidden rounded-full">
                                                                        <img
                                                                            src={previewSrc}
                                                                            alt="Aperçu de votre photo"
                                                                            className="h-full w-full object-cover"
                                                                        />
                                                                    </div>
                                                                    <div className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm">
                                                                        <CheckCircle2 className="h-5 w-5" />
                                                                    </div>
                                                                </div>
                                                                <div className="text-sm font-black text-emerald-700">
                                                                    Photo ajoutée avec succès !
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    Cliquez pour changer de photo
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-primary shadow-md ring-1 ring-border/40">
                                                                    <Camera className="h-6 w-6" />
                                                                </div>
                                                                <div className="text-base font-black">Ajoutez votre photo</div>
                                                                <div className="text-xs text-muted-foreground/90">
                                                                    Choisissez une photo récente où vous souriez
                                                                </div>
                                                            </>
                                                        )}

                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept={ACCEPTED_IMAGE_MIMES}
                                                        className="hidden"
                                                        onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;

                                                            if (file.size > PROFILE_PHOTO_MAX_BYTES) {
                                                                setFileError(
                                                                    intl.formatMessage("generic.upload.too_large", {
                                                                        max: PROFILE_PHOTO_MAX_MEGABYTES,
                                                                    })
                                                                );
                                                                setLocalFile(null);
                                                                form.change("photo", undefined);
                                                                if (fileInputRef.current) fileInputRef.current.value = "";
                                                                return;
                                                            }

                                                            setFileError(null);
                                                            setLocalFile(file);
                                                            const content = (await blobToBase64(file)) as string;

                                                            form.change("photo", {
                                                                name: file.name,
                                                                size: file.size,
                                                                type: file.type,
                                                                content,
                                                            });
                                                        }}
                                                    />

                                                    {!!(values as RegisterDraft | undefined)?.photo && (
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
                                                            onClick={() => {
                                                                setFileError(null);
                                                                setLocalFile(null);
                                                                form.change("photo", undefined);
                                                                if (fileInputRef.current) fileInputRef.current.value = "";
                                                            }}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                            Retirer la photo
                                                        </button>
                                                    )}
                                                </div>
                                                </button>

                                                {!((values as RegisterDraft | undefined)?.photo) && (
                                                    <Button
                                                        type="button"
                                                        className="btn btn--primary inline-flex items-center justify-center rounded-full px-8 py-4 font-black"
                                                        onClick={() => fileInputRef.current?.click()}
                                                    >
                                                        Parcourir mes photos
                                                    </Button>
                                                )}

                                                {sizeError && (
                                                    <div className="mt-3 text-xs font-semibold text-red-600">
                                                        {sizeError}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="w-full rounded-[28px] border border-border/60 bg-primary/5 p-6 text-left">
                                            <div className="flex items-start gap-4">
                                                <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border/40">
                                                    <Info className="h-4 w-4" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-black text-foreground/90">
                                                        Pour une photo réussie, n'oubliez pas que votre photo sera visible sur la plateforme
                                                    </div>
                                                    <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
                                                        <li>✓ Photo récente</li>
                                                        <li>✓ Visage bien visible et souriant</li>
                                                        <li>✓ Éclairage naturel de préférence</li>
                                                        <li>✓ Fond neutre ou contexte agréable</li>
                                                        <li>✓ Veillez à être seul.e sur la photo</li>
                                                    </ul>
                                                    <div className="mt-3 text-[11px] text-muted-foreground">
                                                        Toute photo contrevenant à nos CGU ou à la loi verra le profil supprimé et reporté
                                                        aux autorités le cas échéant.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between text-center margin-top--10">
                                    <Button
                                        className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                        onClick={() => {
                                            setProvider(Object.assign({}, provider, values));
                                            stepper.goTo("informations_profile");
                                        }}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                        {intl.formatMessage("generic.step.previous")}
                                    </Button>
                                    <Button
                                        className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full font-black px-10 py-4"
                                        type="submit"
                                        disabled={submitting || !!sizeError || !savedPhoto}
                                    >
                                        {intl.formatMessage("generic.step.next")}
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>                        
                        </form>
                    );
                }}
            />
        </React.Fragment>
    );
};

export default RegisterProviderPhoto;
